/**
 * Retry generico per insert/update Supabase quando il payload contiene colonne
 * non ancora presenti nello schema (migration non eseguita — PostgREST risponde
 * PGRST204 "Could not find the 'X' column of 'Y' in the schema cache").
 *
 * Rimuove la colonna indicata dall'errore e ripete, finché la scrittura va a
 * buon fine o non ci sono più colonne note da rimuovere. Così un form con
 * molti campi opzionali aggiunti via migration additiva (es. ProductForm)
 * degrada in modo controllato invece di bloccare l'intero salvataggio.
 *
 * NON usare per colonne obbligatorie: se la colonna mancante è necessaria al
 * dominio, il chiamante deve gestirla esplicitamente.
 */

const SCHEMA_CACHE_COLUMN_RE = /Could not find the '([^']+)' column of '[^']+' in the schema cache/;

interface SupabaseWriteResult<T> {
  data: T | null;
  error: { code?: string; message: string } | null;
}

export async function writeWithSchemaFallback<T>(
  attempt: (payload: Record<string, unknown>) => Promise<SupabaseWriteResult<T>>,
  payload: Record<string, unknown>,
  maxRetries = 10
): Promise<SupabaseWriteResult<T>> {
  let currentPayload = payload;
  let result: SupabaseWriteResult<T>;
  let attempts = 0;

  do {
    result = await attempt(currentPayload);
    if (!result.error) return result;

    const isSchemaCacheMiss = result.error.code === 'PGRST204';
    const match = isSchemaCacheMiss ? result.error.message.match(SCHEMA_CACHE_COLUMN_RE) : null;
    if (!match || !(match[1] in currentPayload)) return result;

    const { [match[1]]: _omit, ...rest } = currentPayload;
    currentPayload = rest;
    attempts++;
  } while (attempts <= maxRetries);

  return result;
}
